import { AppWindow, CheckCircle, Layers, RefreshCw, TriangleAlert, UsersRound, X } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { KPICard } from '../Dashboard/KPICard';
import { BarChart } from '../Dashboard/BarChart';
import { DoughnutChart } from '../Dashboard/DoughnutChart';
import { SystemHealthCard } from './SystemHealthCard';
import { ResourcesCapacityCard } from './ResourcesCapacityCard';
import { ForgeStatsCard } from './ForgeStatsCard';
import { Button } from '../Common/button';
import type { ForgeDashboardData } from '../../services/forgeDashboardService';
import { ComponentError } from '../../types';
import { cn } from '../../lib/utils';

interface OverviewTabProps {
  dashboardData: ForgeDashboardData | null;
  loading: boolean;
  lastRefresh: Date | null;
  onRefresh: () => void;
}

const WarningBanner: React.FC<{
  errors: ComponentError[];
  getComponentDisplayName: (c: string) => string;
  onDismiss: (c: string) => void;
  t: (k: string, f?: string) => string;
}> = ({ errors, getComponentDisplayName, onDismiss, t }) => {
  if (errors.length === 0) return null;
  return (
    <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <TriangleAlert className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            {t('forgeDashboard.warnings.title', 'Some data could not be loaded')}
          </h4>
          <div className="mt-2 space-y-1">
            {errors.map((error) => (
              <div
                key={error.component}
                className="flex items-center justify-between text-sm text-yellow-700 dark:text-yellow-300"
              >
                <span>
                  <strong>{getComponentDisplayName(error.component)}:</strong> {error.message}
                  {error.status_code && (
                    <span className="text-yellow-600 dark:text-yellow-400 ml-1">
                      (HTTP {error.status_code})
                    </span>
                  )}
                </span>
                <button
                  onClick={() => onDismiss(error.component)}
                  className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200 p-1"
                  title={t('forgeDashboard.warnings.dismiss', 'Dismiss')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const OverviewTab: React.FC<OverviewTabProps> = ({
  dashboardData,
  loading,
  lastRefresh,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const [dismissedWarnings, setDismissedWarnings] = useState<Set<string>>(new Set());

  const activeErrors = (dashboardData?.errors || []).filter(
    (error) => !dismissedWarnings.has(error.component)
  );

  const getComponentDisplayName = (component: string) => {
    switch (component) {
      case 'health':
        return t('forgeDashboard.warnings.healthComponent', 'System Health');
      case 'resources':
        return t('forgeDashboard.warnings.resourcesComponent', 'Cluster Resources');
      case 'usage':
        return t('forgeDashboard.warnings.usageComponent', 'Resource Usage');
      case 'stats':
        return t('forgeDashboard.warnings.statsComponent', 'Statistics');
      default:
        return component;
    }
  };

  const getForgeStatusChartData = () => {
    if (!dashboardData?.stats) return { labels: [], datasets: [] };
    const forgeStatus = dashboardData.stats.forges_by_status;
    return {
      labels: [
        t('forgeDashboard.stats.deployed', 'Deployed'),
        t('forgeDashboard.stats.deploying', 'Deploying'),
        t('forgeDashboard.stats.creating', 'Creating'),
        t('forgeDashboard.stats.error', 'Error'),
      ],
      datasets: [
        {
          label: t('forgeDashboard.charts.forgesByStatus', 'Forges by Status'),
          data: [
            forgeStatus.deployed,
            forgeStatus.deploying,
            forgeStatus.creating,
            forgeStatus.error,
          ],
          backgroundColor: [
            'hsl(142.1 76.2% 36.3%)',
            'hsl(221.2 83.2% 53.3%)',
            'hsl(47.9 95.8% 53.1%)',
            'hsl(0 84.2% 60.2%)',
          ],
        },
      ],
    };
  };

  const getAppHealthChartData = () => {
    if (!dashboardData?.stats) return { labels: [], datasets: [] };
    return {
      labels: [
        t('forgeDashboard.stats.healthyApps', 'Healthy'),
        t('forgeDashboard.stats.unhealthyApps', 'Unhealthy'),
      ],
      datasets: [
        {
          data: [
            dashboardData.stats.healthy_applications,
            dashboardData.stats.unhealthy_applications,
          ],
          backgroundColor: ['hsl(142.1 76.2% 36.3%)', 'hsl(0 84.2% 60.2%)'],
        },
      ],
    };
  };

  const appHealthKnown =
    (dashboardData?.stats?.healthy_applications || 0) +
    (dashboardData?.stats?.unhealthy_applications || 0);
  const appHealthUnknown =
    (dashboardData?.stats?.total_applications || 0) - appHealthKnown;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {t('forgeDashboard.loading', 'Loading Forge Dashboard...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {dashboardData?.status && dashboardData.status !== 'success' && (
          <span
            className={cn(
              'px-2 py-0.5 rounded-full text-xs font-medium',
              dashboardData.status === 'partial'
                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-400'
                : 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400'
            )}
          >
            {dashboardData.status === 'partial'
              ? t('forgeDashboard.status.partial', 'Partial Data')
              : t('forgeDashboard.status.error', 'Data Error')}
          </span>
        )}
        <div className="flex items-center gap-4 ml-auto">
          {lastRefresh && (
            <span className="text-xs text-muted-foreground">
              {t('forgeDashboard.lastRefresh', 'Last refresh')}: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={onRefresh} className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            {t('global.refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      <WarningBanner
        errors={activeErrors}
        getComponentDisplayName={getComponentDisplayName}
        onDismiss={(c) => setDismissedWarnings((prev) => new Set([...prev, c]))}
        t={t}
      />

      {dashboardData?.stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title={t('forgeDashboard.kpi.totalClients', 'Total Clients')}
            value={dashboardData.stats.total_clients}
            icon={UsersRound}
          />
          <KPICard
            title={t('forgeDashboard.kpi.totalForges', 'Total Forges')}
            value={dashboardData.stats.total_forges}
            icon={Layers}
          />
          <KPICard
            title={t('forgeDashboard.kpi.totalApplications', 'Total Applications')}
            value={dashboardData.stats.total_applications}
            icon={AppWindow}
          />
          <KPICard
            title={t('forgeDashboard.kpi.healthyApps', 'Healthy Apps')}
            value={`${dashboardData.stats.healthy_applications}/${dashboardData.stats.total_applications}`}
            icon={CheckCircle}
          />
        </div>
      )}

      {dashboardData?.health?.data && <SystemHealthCard health={dashboardData.health.data} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {dashboardData?.resources && (
          <ResourcesCapacityCard resources={dashboardData.resources} usage={dashboardData.usage} />
        )}
        {dashboardData?.stats && <ForgeStatsCard stats={dashboardData.stats} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <BarChart
            data={getForgeStatusChartData()}
            title={t('forgeDashboard.charts.forgesByStatus', 'Forges by Status')}
          />
        </div>
        <div>
          {appHealthKnown === 0 ? (
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                {t('forgeDashboard.charts.applicationHealth', 'Application Health')}
              </h3>
              <div className="h-64 flex flex-col items-center justify-center text-center">
                <AppWindow className="h-10 w-10 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {dashboardData?.stats?.total_applications
                    ? t(
                        'forgeDashboard.charts.applicationHealthUnknown',
                        'Health status is unknown for all {{count}} application(s).',
                        { count: appHealthUnknown }
                      )
                    : t(
                        'forgeDashboard.charts.applicationHealthEmpty',
                        'No applications yet.'
                      )}
                </p>
              </div>
            </div>
          ) : (
            <DoughnutChart
              data={getAppHealthChartData()}
              title={t('forgeDashboard.charts.applicationHealth', 'Application Health')}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
